import { useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';

import { selectedDeviceAtom } from '@/components/common/MapView';
import { devicesAtom, patchDeviceNameAtom } from '@/interfaces/devices';
import { getErrInfo } from '@/components/common/utils';
import DeviceDetailAlarmSettings from '@/components/devices/DeviceDetailAlarmSettings';

export default function DeviceDetailSettings() {
  const [device, setDevice] = useAtom(selectedDeviceAtom);
  const { mutate, status, error } = useAtomValue(patchDeviceNameAtom);
  const { refetch } = useAtomValue(devicesAtom);

  const [name, setName] = useState<string>(device?.name);
  const { t } = useTranslation();

  useEffect(() => {
    if (device) {
      setName(device.name);
    }
  }, [device]);

  useEffect(() => {
    if (status === 'success') {
      refetch();
      setDevice((prev) => ({ ...prev, name }));
    } else if (error && status === 'error') {
      const errInfo = getErrInfo(error);

      let errMsgKey = errInfo.headerCode ? 'Common.message.serverErrorOccurred' : 'Common.message.unknownErrorOccurred';
      switch (errInfo.bodyCode) {
        case 'SENSOR_NOT_FOUND':
          errMsgKey = 'Devices.message.notFound';
          break;
        case 'SENSOR_NAME_ALREADY_EXIST':
          errMsgKey = 'Devices.message.alreadyExist';
          break;
        default:
          errMsgKey = errInfo.commonMsgKey !== null && errInfo.commonMsgKey;
          break;
      }

      window.alert(t(errMsgKey));
    }
  }, [error, name, refetch, setDevice, status, t]);

  const onChangeClick = (): void => {
    mutate({ id: device.id, name: name });
  };

  return (
    <Stack gap={4} sx={{ flexGrow: 1, overflow: 'auto' }}>
      <Box display="grid" gridTemplateColumns={'auto 200px'} columnGap={4}>
        <TextField
          id="Name"
          label={t('Common.label.name')}
          variant="standard"
          value={name}
          fullWidth={true}
          onChange={(e) => setName(e.target.value)}
        />
        <Button
          variant="outlined"
          disabled={!(name.length > 0 && name.trim() !== device?.name)}
          onClick={onChangeClick}
          sx={{
            width: 200,
            height: 44,
            borderRadius: 22,
          }}
        >
          <Typography fontWeight="medium">{t('Common.label.saveButton')}</Typography>
        </Button>
      </Box>

      <DeviceDetailAlarmSettings />
    </Stack>
  );
}
